// Einstellungen → Reiter „Anzeige" (docs/i18n.md §2: dieser Reiter ist IN, die
// übrigen Reiter — Konto, E-Mail, Kalender, KI-Modelle, Integrationen, Admin —
// bleiben vorerst deutsch).
//
// Die REITERZEILE selbst bleibt in beiden Sprachen deutsch: sie ist eine Einheit,
// und ein einzelnes englisches Wort zwischen sechs deutschen wäre schlechter als
// ein ehrlich unübersetzter Streifen (docs/i18n.md §4.7).
//
// Die Auswahl-Listen des Reiters (Presets, Schriftgrößen, Quellen-Zustände,
// Aufgaben-Ansichten) liegen als Wert-Tabellen in ui/theme.tsx, ui/fontScale.tsx,
// ui/sourcesPref.tsx und ui/taskViewPref.tsx. Dort steht die STRUKTUR, hier der
// TEXT — indiziert über denselben `value` (Muster ui/labels.ts).
export const settings = {
  // ── Kopf der Seite ───────────────────────────────────────────────────────
  // `kopfTitel` ist wortgleich mit `shared.rahmen.einstellungen` (dem Eintrag
  // im Profil-Menü, der hierher führt) — bewusst zwei Keys: Menü-Eintrag und
  // Seiten-Überschrift gehören verschiedenen Bausteinen.
  kopfTitel: "Einstellungen",
  // Der Untertitel zählt die Reiter auf; der Zusatz erscheint nur für Admins.
  // Als FUNKTION statt zwei Bruchstücken, damit der Satz in beiden Sprachen an
  // einem Stück steht — im Englischen fiele der Einschub sonst anders aus.
  kopfUntertitel: (istAdmin: boolean) =>
    "Profil, Darstellung, E-Mail, Kalender, KI-Modelle und externe Integrationen" +
    (istAdmin ? " — und, als Atlas-Admin, org-weite Einstellungen." : "."),

  spracheTitel: "Sprache / Language",
  spracheHinweis:
    "Sprache der Oberfläche. Wirkt sofort und gilt geräteübergreifend. " +
    "Die Umstellung greift schrittweise: die Standard-Bereiche zuerst, " +
    "die Studio-Bereiche bleiben vorerst deutsch.",
  spracheHinweisMail:
    "Erzeugte Mail-Antworten und Nachfass-Texte folgen weiterhin der Sprache " +
    "der eingehenden Nachricht — nicht dieser Einstellung.",
  spracheHinweisDatum: "Datums- und Zahlenformate bleiben deutsch (TT.MM.JJJJ).",
  // Sprachnamen stehen in IHRER EIGENEN Sprache und werden deshalb NICHT
  // übersetzt (dieselbe Zeile in beiden Katalogen). Wer die Oberfläche gerade
  // nicht lesen kann, findet so trotzdem seine Sprache.
  spracheDeutsch: "Deutsch",
  spracheEnglisch: "English",
  spracheSpeichert: " · speichere …",
  spracheFehler: "Sprache konnte nicht gespeichert werden",

  // ── Erscheinungsbild ─────────────────────────────────────────────────────
  erscheinungsbildTitel: "Erscheinungsbild",
  erscheinungsbildHinweis: "Farbwelt und Hell-/Dunkel-Modus. Gilt pro Browser.",
  // Preset-Namen: „Lab" und „Warm" sind in beiden Sprachen dasselbe Wort.
  presetName: {
    lab: "Lab",
    warm: "Warm",
    weiss: "Weiß",
  },
  presetHinweis: {
    lab: "Klar & wissenschaftlich",
    warm: "Der bisherige Atlas-Look",
    weiss: "Reines Weiß, Abgrenzung über Linien",
  },
  presetEigenes: "Eigenes",
  presetEigenesHinweis: "Akzent selbst wählen",
  akzentfarbe: "Akzentfarbe",
  akzentfarbeHinweis: "Rampe wird automatisch berechnet.",
  canvasTon: "Canvas-Ton",
  canvasReinweiss: "Reinweiß",
  canvasGetoent: "Leicht getönt",
  linien: "Linien",
  linienDezent: "Dezent",
  linienKraeftig: "Kräftig",
  modus: {
    light: "Hell",
    dark: "Dunkel",
    system: "System",
  },

  // ── Schriftgröße ─────────────────────────────────────────────────────────
  schriftgroesseTitel: "Schriftgröße",
  schriftgroesseHinweis:
    "Skaliert die gesamte UI. Wirkt sofort und wird pro Browser gespeichert.",
  schriftgroesse: {
    s: "Klein",
    m: "Mittel",
    l: "Groß",
    xl: "Sehr groß",
  },
  schriftgroesseFehler: "Schriftgröße konnte nicht persistiert werden",
  // Hängt an einen laufenden Speichervorgang an — dieselbe Form wie
  // `spracheSpeichert`, aber ein eigener Eintrag (jede Sektion sagt es selbst).
  speichert: " · speichere …",

  // ── Schriftart ───────────────────────────────────────────────────────────
  // Die Wert-Tabelle (Stacks, Reihenfolge) steht in ui/uiFont.tsx, hier nur der
  // TEXT — indiziert über denselben `value` (Muster ui/labels.ts).
  schriftartTitel: "Schriftart",
  schriftartHinweis:
    "Gilt für die gesamte Oberfläche. Wirkt sofort und gilt geräteübergreifend.",
  schriftartFehler: "Schriftart konnte nicht gespeichert werden",
  // Steht nur an Aptos: die Schrift ist nicht mitgeliefert (Microsoft-Lizenz),
  // sie muss auf dem Rechner liegen. Der Hinweis erscheint gemessen und nicht
  // vermutet (Breiten-Probe in ui/uiFont.tsx::fontInstalliert).
  schriftartNichtInstalliert:
    "Auf diesem Gerät nicht installiert — es rendert Segoe UI. (IT kann Aptos verteilen.)",
  // Schriftnamen sind Eigennamen und stehen deshalb in BEIDEN Katalogen gleich
  // (dieselbe Regel wie „Deutsch"/„English" oben, docs/i18n.md §3).
  schriftart: {
    inter: { label: "Inter", hinweis: "Die Atlas-Standardschrift." },
    system: {
      label: "System (Segoe UI)",
      hinweis: "Die Schrift von Windows — es wird nichts nachgeladen.",
    },
    "open-sans": { label: "Open Sans", hinweis: "Offen und ruhig, sehr geläufig." },
    aptos: { label: "Aptos", hinweis: "Die Office-Standardschrift seit 2024." },
    "ibm-plex": { label: "IBM Plex Sans", hinweis: "Technisch, mit klaren Ziffern." },
    "source-sans": { label: "Source Sans 3", hinweis: "Kompakter — mehr Text pro Zeile." },
    atkinson: {
      label: "Atkinson Hyperlegible",
      hinweis: "Barrierefrei: geschlitzte Null, klar getrennte Formen.",
    },
  },

  // ── Quellen-Anzeige im Chat ──────────────────────────────────────────────
  quellenTitel: "Quellen-Anzeige im Chat",
  quellenHinweis: "Wie die Quellen unter einer Antwort standardmäßig dargestellt werden.",
  quellenFehler: "Quellen-Default konnte nicht persistiert werden",
  quellen: {
    collapsed: {
      label: "Zugeklappt",
      hinweis: "Nur 'N Quellen'-Hinweis, alles weitere auf Klick.",
    },
    titles: {
      label: "Titel",
      hinweis: "Quellen-Liste offen, Karten als Titel-Zeile.",
    },
    expanded: {
      label: "Mit Inhalt",
      hinweis: "Quellen-Liste offen, alle Karten ausgeklappt mit Chunk.",
    },
  },

  // ── Aufgaben-Ansicht ─────────────────────────────────────────────────────
  aufgabenAnsichtTitel: "Aufgaben-Ansicht",
  aufgabenAnsichtHinweis:
    "Wie eine Aufgabe beim Klick auf eine Kanban-Karte zuerst geöffnet wird. " +
    "Im Aufgaben-Fenster jederzeit per Kopf-Umschalter wechselbar. Gilt pro Browser.",
  aufgabenAnsicht: {
    compact: {
      label: "Kompakt",
      hinweis: "Nur die Kernfelder: Titel, Status, Fällig, Workstream, Notizen.",
    },
    detailed: {
      label: "Detailliert",
      hinweis: "Alles: Ziel, Acceptance, Unteraufgaben, Zuweisung, Activity.",
    },
  },

  // ── Seitenleiste ─────────────────────────────────────────────────────────
  seitenleisteTitel: "Seitenleiste",
  seitenleisteAusgeklappt: "Projekte in der Seitenleiste ausgeklappt starten",
};

export type SettingsKatalog = typeof settings;
